<template>
  <div class="rhg-instructions-map__menu">
    <div class="rhg-instructions-map__form">
      <label for="startPoint" class="rhg-instructions-map__search-bar">
        <input
          ref="autocomplete"
          id="startPoint"
          name="startPoint"
          class="rhg-instructions-map__search-input"
          type="text"
          :placeholder="translate('weStartingPoint')"
          :aria-label="translate('weWhereDoYouGo')"
          v-model="searchParam">
        <button
          v-if="showDelete"
          class="rhg-instructions-map__delete-button"
          @click="handleDeleteSearch"
        >
          <i class="rhg-font-icon--close"></i>
        </button>

        <ErrorMessage v-if="showErrorMessage" :message="translate('weErrorMsgDirections')" />
      </label>
    </div>
    <div class="rhg-instructions-map__tabs-container" @mousedown="onMouseDown" @mouseup="onMouseUp" @mousemove="onMouseMove">
      <ul class="rhg-instructions-map__tabs-wrapper"
        ref="horizontal"
        @scroll="onScroll"
      >
        <li>
          <label
            class="rhg-instructions-map__tab"
            :for="`mode-${travelModeOptions.DRIVING}`"
            @click="onTravelModeClick($event, travelModeOptions.DRIVING)"
            :class="{
              active: travelMode === 'DRIVING',
              disabled: !this.hasTravelTime(drivingTime)
            }"
          >
            <input
              :id="`mode-${travelModeOptions.DRIVING}`"
              type="radio"
              class="rhg-instructions-map__radio-input"
              v-model="travelMode"
              name="travelMode"
              :value="travelModeOptions.DRIVING"
              :disabled="!this.hasTravelTime(drivingTime)"
              autocomplete="off"
              @change="handleTravelModeSelection"
            >
            <div class="rhg-instructions-map__tab-information">
              <i class="rhg-font-icon--car-rental"></i>
              <p v-if="hasTravelTime(drivingTime)">
                <span v-if="drivingTime && drivingTime.hours">
                  {{ translate('desHoursAcronym', undefined, drivingTime.hours) }}
                </span>
                  <span v-if="drivingTime && drivingTime.minutes">
                  {{ translate('desMinutesAcronym', undefined, drivingTime.minutes) }}
                </span>
              </p>
            </div>
          </label>
        </li>

        <li>
          <label
            class="rhg-instructions-map__tab"
            :for="`mode-${travelModeOptions.TRANSIT}`"
            @click="onTravelModeClick($event, travelModeOptions.TRANSIT)"
            :class="{
              active: travelMode === 'TRANSIT',
              disabled: !this.hasTravelTime(transitTime)
            }"
          >
            <input
              :id="`mode-${travelModeOptions.TRANSIT}`"
              type="radio"
              class="rhg-instructions-map__radio-input"
              v-model="travelMode"
              name="travelMode"
              :value="travelModeOptions.TRANSIT"
              :disabled="!this.hasTravelTime(transitTime)"
              autocomplete="off"
              @change="handleTravelModeSelection"
            >
            <div class="rhg-instructions-map__tab-information">
              <i class="rhg-font-icon--bus"></i>
              <p v-if="hasTravelTime(transitTime)">
                <span v-if="transitTime && transitTime.hours">
                  {{ translate('desHoursAcronym', undefined, transitTime.hours) }}
                </span>
                  <span v-if="transitTime && transitTime.minutes">
                  {{ translate('desMinutesAcronym', undefined, transitTime.minutes) }}
                </span>
              </p>
            </div>
          </label>
        </li>

        <li>
          <label
            class="rhg-instructions-map__tab"
            :for="`mode-${travelModeOptions.WALKING}`"
            @click="onTravelModeClick($event, travelModeOptions.WALKING)"
            :class="{
              active: travelMode === 'WALKING',
              disabled: !this.hasTravelTime(walkingTime)
            }"
          >
            <input
              :id="`mode-${travelModeOptions.WALKING}`"
              type="radio"
              class="rhg-instructions-map__radio-input"
              v-model="travelMode"
              name="travelMode"
              :value="travelModeOptions.WALKING"
              :disabled="!hasTravelTime(walkingTime)"
              autocomplete="off"
              @change="handleTravelModeSelection"
            >
            <div class="rhg-instructions-map__tab-information">
              <i class="rhg-font-icon--walk"></i>
              <p v-if="hasTravelTime(walkingTime)">
                <span v-if="walkingTime && walkingTime.hours">
                  {{ translate('desHoursAcronym', undefined, walkingTime.hours) }}
                </span>
                  <span v-if="walkingTime && walkingTime.minutes">
                  {{ translate('desMinutesAcronym', undefined, walkingTime.minutes) }}
                </span>
              </p>
            </div>
          </label>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import ErrorMessage from './components/ErrorMessage'

export default {
  name: 'Menu',
  components: {
    ErrorMessage
  },
  props: {
    googleMaps: Object,
    travelModeOptions: Object,
    drivingTime: Object,
    walkingTime: Object,
    transitTime: Object,
    timesLoaded: Object,
    centerCoordinates: Object
  },
  data: () => ({
    searchParam: '',
    selectedTravelMode: '',
    travelMode: '',
    showDelete: false,
    numberOfResults: 0,
    dragData: {
      left: 0,
      originX: 0,
      originLeft: 0
    },
    isDragging: false,
    autocomplete: null,
    pointSaved: null
  }),
  async mounted() {
    /* Bounds to show biased results on inmediate sorrounding of the map first. */
    /* 0.5 degrees range on latitude. Aprox. 55.55 km. 27.8 km on either side to the north and south */
    /* 0.5 degrees range on longitude. Aprox. 44 km. 22 km on either side to the west and east */
    const southwest = { lat: this.centerCoordinates.lat - 0.25, lng: this.centerCoordinates.lng - 0.25 }
    const northeast = { lat: this.centerCoordinates.lat + 0.25, lng: this.centerCoordinates.lng + 0.25 }
    const autocompleteBounds = new this.googleMaps.LatLngBounds(southwest, northeast)

    this.autocomplete = await new this.googleMaps.places.Autocomplete(this.$refs.autocomplete, {
      bounds: autocompleteBounds
    })
  },
  updated() {
    if (this.isTimeLoaded && (this.hasTravelTime(this.drivingTime) || this.hasTravelTime(this.transitTime) || this.hasTravelTime(this.walkingTime))) {
      this.handleTravelModeUpdate()
    }
  },
  methods: {
    handleOriginPointSelected(pointSelected) {
      this.pointSaved = {
        pointName: this.searchParam,
        coordinates: {
          lat: pointSelected.geometry.location.lat(),
          lng: pointSelected.geometry.location.lng()
        }
      }
      this.$emit('onOriginPontSelected', this.pointSaved)
    },
    handleTravelModeUpdate() {
      if (!this.hasTravelTime(this.drivingTime) && this.selectedTravelMode === '') {
        this.changeTravelMode(this.travelModeOptions.TRANSIT)
      } else if (!this.hasTravelTime(this.transitTime) && !this.hasTravelTime(this.drivingTime) && this.selectedTravelMode === '') {
        this.changeTravelMode(this.travelModeOptions.WALKING)
      } else {
        this.selectedTravelMode = this.selectedTravelMode || this.travelModeOptions.DRIVING
        this.changeTravelMode(this.selectedTravelMode)
      }
    },
    changeTravelMode(travelMode) {
      this.travelMode = travelMode
      this.$emit('onTravelModeSelection', travelMode)
    },
    handleTravelModeSelection(e) {
      this.changeTravelMode(e.target.value)
    },
    hasTravelTime(travelTime) {
      return Object.values(travelTime || {}).filter(Boolean).length
    },
    handleDeleteSearch() {
      this.searchParam = ''
      this.showDelete = false
      this.travelMode = ''
      this.selectedTravelMode = ''
      this.numberOfResults = 0
      this.pointSaved = null
      this.$emit('onDeleteSearch')
    },
    onTravelModeClick(e, travelMode) {
      if (this.isDragging) { return }
      this.selectedTravelMode = travelMode
      const offset = e.pageX - this.dragData.originX
      const left = this.dragData.originLeft - offset
      this.$refs.horizontal.scrollTo(left, 'auto')
    },
    onScroll() {
      this.dragData.left = this.$refs.horizontal.scrollLeft
    },
    onMouseDown(e) {
      this.isDragging = true
      this.$refs.horizontal.classList.add('dragging')
      this.dragData.originX = e.pageX
      this.dragData.originLeft = this.dragData.left
    },
    onMouseUp() {
      this.$refs.horizontal.classList.remove('dragging')
      this.isDragging = false
    },
    onMouseMove(e) {
      const offset = e.pageX - this.dragData.originX
      const left = this.dragData.originLeft - offset
      this.$refs.horizontal.scrollTo(left, 'auto')
    }
  },
  computed: {
    showErrorMessage() {
      const loadedTime = this.isTimeLoaded
      return (!this.hasTravelTime(this.drivingTime) && !this.hasTravelTime(this.transitTime) && !this.hasTravelTime(this.walkingTime) && loadedTime)
    },
    isTimeLoaded() {
      const { walking, driving, transit } = this.timesLoaded
      return walking && driving && transit
    }
  },
  watch: {
    searchParam: function(newVal, oldVal) {
      if (this.searchParam === '' || (this.pointSaved && this.pointSaved.pointName !== newVal)) {
        this.handleDeleteSearch()
        this.searchParam = newVal
        this.$refs.autocomplete.value = this.searchParam
        this.showDelete = false
      } else {
        this.showDelete = true
      }

      this.googleMaps.event.addListener(this.autocomplete, 'place_changed', () => {
        this.searchParam = this.$refs.autocomplete.value
        this.handleOriginPointSelected(this.autocomplete.getPlace())
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.rhg-instructions-map {
  &__tabs-container {
    align-self: unset;

    @media (min-width: 992px) {
      align-self: center;
    }
  }

  &__menu {
    display: flex;
    gap: 20px;
    flex-direction: column;

    @media (min-width: 992px) {
      gap: 36px;
      flex-direction: row;
    }
  }

  &__form {
    display: flex;
    padding: 20px 16px 0 16px;

    @media (min-width: 992px) {
      max-width: 516px;
      gap: 36px;
      flex-direction: row;
      padding: 0;
    }

    label {
      width: 100%;
      flex: 1;
    }
  }

  &__search-input {
    border: 1px solid color('medium-grey');
    padding: 8px 16px;
    padding-inline-end: 38px;
    border-radius: 40px;
    background: transparent;
    font-size: 14px;
    line-height: 20px;
    display: flex;
    align-items: center;
    height: 44px;
    width: 100%;

    @media (min-width: 992px) {
      padding: 10px 24px;
      padding-inline-end: 38px;
      font-size: 16px;
      line-height: 15px;
      height: 48px;
      width: 500px;
    }
  }

  &__tabs-wrapper {
    display: flex;
    gap: 16px;
    align-items: center;
    overflow-x: auto;
    scroll-behavior: smooth;
    padding: 0 16px 16px 16px;

    @media (min-width: 992px) {
      padding: 0;
    }
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }
    &.dragging{scroll-behavior:unset;}
  }

  &__tab {
    color: color('black-discovery');
    border: 2px solid color('black-discovery');
    background-color: #fff;
    border-radius: 28px;
    padding: 4px 16px;
    white-space: normal;
    min-width: 122px;
    cursor: pointer;

    @media (hover: hover) {
      &:hover {
        background: color('black-discovery');
        color: #fff;
        font-weight: 700;
      }
    }

    i[class*="rhg-font-icon--"] {
      width: 24px;
      height: 24px;
      size: 24px;
    }

    span {
      font-size: 16px;
      line-height: 22px;
      width: max-content;
    }
  }

  &__radio-input {
    position: absolute;
  }

  &__tab-information {
    display: grid;
    grid-template-columns: auto auto;
    align-items: center;
    min-width: max-content;
    justify-content: center;

    p {
      margin: 0;
      margin-inline-start: 13px;
      font-weight: inherit;
    }
  }

  &__search-bar {
    position: relative;
  }

  &__delete-button {
    position: absolute;
    right: 6px;
    top: 6px;
    width: 32px;
    height: 32px;
    border: none;
    background-color: color('light-grey');
    border-radius: 50px;
    padding: 0;

    @media (min-width: 992px) {
      right: 7px;
      top: 8px;
    }

    &:focus {
      box-shadow: none;
    }

    i {
      width: 12px;
      height: 12px;
      font-size: 12px;
      margin: 0 auto;
      margin-bottom: 2px;
      color: color('black-light');
    }
  }

  .active {
    background: color('black-discovery');
    color: #fff;
    font-weight: 700;
  }

  .disabled {
    opacity: 0.5;
  }
}

li {
  list-style: none;
}

/* HIDE RADIO AND CHECKBOX only iOs*/
@supports (-webkit-touch-callout: none) {
  input[type='radio'],
  input[type='checkbox'] {
    margin: 0 !important;
    padding: 0 !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    width: 0;
    height: 0;
    border: 0;
  }
}

html[dir="rtl"]{
  .rhg-instructions-map {
    &__delete-button {
      right: unset;
      left: 6px;

      @media (min-width: 992px) {
        left: 7px;
      }
    }
  }
}
</style>
